import { useState } from "react";
import { useTranslation } from "react-i18next";
import "./App.css";
import { Select } from "./Components/Select.tsx";

export interface AIRoomSettingsValues {
  craziness: number;
  sexualScale: number;
  drinkAmount: number;
}

interface AIRoomSettingsProps {
  roomName: string;
  sessionId: string;
  socket: any;
  initialSettings: AIRoomSettingsValues;
  initialStep?: number;
  onDone: () => void;
  onBack: () => void;
}

function AIRoomSettings({
  roomName,
  sessionId,
  socket,
  initialSettings,
  initialStep = 0,
  onDone,
  onBack,
}: AIRoomSettingsProps) {
  const { t } = useTranslation();

  const [craziness, setCraziness] = useState(initialSettings.craziness);
  const [sexualScale, setSexualScale] = useState(initialSettings.sexualScale);
  const [drinkAmount, setDrinkAmount] = useState(initialSettings.drinkAmount);
  const [step, setStep] = useState(initialStep); // 0 = craziness+sexual, 1 = drink amount

  // Every selection is saved immediately (fire-and-forget), as the pre-rework
  // setup wizard did; NESTE only moves on.
  const emitSettings = (next: AIRoomSettingsValues) => {
    socket.emit("ai room set settings", { roomName, sessionId, ...next });
  };
  const applyCraziness = (value: number) => {
    setCraziness(value);
    emitSettings({ craziness: value, sexualScale, drinkAmount });
  };
  const applySexualScale = (value: number) => {
    setSexualScale(value);
    emitSettings({ craziness, sexualScale: value, drinkAmount });
  };
  const applyDrinkAmount = (value: number) => {
    setDrinkAmount(value);
    emitSettings({ craziness, sexualScale, drinkAmount: value });
  };

  // Selections are already saved as they happen; NESTE only moves on.
  const submit = () => onDone();

  const topBar = (onBackClick: () => void) => (
    <div className="container-row lift-topbar">
      <div className="container-row-start">
        <div
          className="button-tertiary-m button-text-m text-color-black"
          onClick={onBackClick}
          style={{ maxHeight: "32.5px" }}
        >
          {t("back")}
        </div>
      </div>
      <div className="text-h3 text-color-black left-margin-s text-force-break">
        {roomName}
      </div>
    </div>
  );

  if (step === 0) {
    return (
      <div className="App-header">
        {topBar(onBack)}

        <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <div style={{ marginTop: 32 }}>
            <p
              className="text-h2 text-color-black"
              style={{ marginBottom: 24 }}
            >
              {t("ai.settings.crazinessTitle")}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { label: t("ai.settings.crazy.calm"), value: 1 },
                { label: t("ai.settings.crazy.medium"), value: 2 },
                { label: t("ai.settings.crazy.high"), value: 3 },
                { label: t("ai.settings.crazy.extreme"), value: 4 },
              ].map((opt) => (
                <div
                  key={opt.value}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Select
                    label={opt.label}
                    value={opt.value}
                    current={craziness}
                    onSelect={applyCraziness}
                  />
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 64 }}>
            <p
              className="text-h2 text-color-black"
              style={{ marginBottom: 24 }}
            >
              {t("ai.settings.sexualTitle")}
            </p>
            <input
              style={{ padding: "0px 6px 0px 6px" }}
              className="square-slider"
              type="range"
              min={0}
              max={100}
              step={1}
              value={sexualScale}
              onChange={(e) => setSexualScale(Number(e.target.value))}
              onMouseUp={(e) => applySexualScale(Number(e.currentTarget.value))}
              onTouchEnd={(e) => applySexualScale(Number(e.currentTarget.value))}
            />
          </div>
        </div>

        <div
          className="button-primary black-fill button-text-l text-color-pink shadow-pink box-button-center"
          onClick={() => setStep(1)}
        >
          {t("next")}
        </div>
      </div>
    );
  }

  // Step 1: Drink amount
  return (
    <div className="App-header">
      {topBar(() => setStep(0))}

      <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ marginTop: 32 }}>
          <p
            className="text-h2 text-color-black"
            style={{ marginBottom: 24, lineHeight: 1.1 }}
          >
            {t("ai.settings.drinkTitle")}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { label: t("ai.settings.drink.calm"), value: 1 },
              { label: t("ai.settings.drink.medium"), value: 2 },
              { label: t("ai.settings.drink.heavy"), value: 3 },
              { label: t("ai.settings.drink.chuglife"), value: 4 },
            ].map((opt) => (
              <div
                key={opt.value}
                style={{ display: "flex", alignItems: "center" }}
              >
                <Select
                  label={opt.label}
                  value={opt.value}
                  current={drinkAmount}
                  onSelect={applyDrinkAmount}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div
        className="button-primary black-fill button-text-l text-color-pink shadow-pink box-button-center"
        onClick={submit}
      >
        {t("next")}
      </div>
    </div>
  );
}

export default AIRoomSettings;
